import { UI } from '@skedulo/protobuf-web'
import { compareByRelevance } from '../../utils'
import { UiApiCrudClientBaseV1 } from '../crud-client-base'
import { CacheFactory } from './cache'
import type {
  IApiTemplate,
  ITemplateCreateRequest,
  ITemplateDelRequest,
  ITemplateGetManyRequest,
  ITemplateGetRequest,
  ITemplatePutRequest,
} from '../../../types'

const BASE_PATH = '/v1/templates'
const TEMPLATES_CACHE_NAME = 'Templates'

const TemplatesCache = CacheFactory.create<
  ITemplateGetManyRequest,
  IApiTemplate[]
>(
  TEMPLATES_CACHE_NAME,
  true, // enablePersistence
  true, // enableSWR
)

export class TemplateUiApiClientV1 extends UiApiCrudClientBaseV1<IApiTemplate> {
  create = async (request: ITemplateCreateRequest) =>
    this.restPost<IApiTemplate>(BASE_PATH, request.template)

  get = async (request: ITemplateGetRequest) =>
    this.getItem(
      await this.restGet<IApiTemplate>(`${BASE_PATH}/${request.uid}`),
    )

  put = async (request: ITemplatePutRequest) =>
    this.mergeAndPut(BASE_PATH, request.template)

  del = async (request: ITemplateDelRequest) =>
    this.restDelete(`${BASE_PATH}/${request.uid}`)

  getMany = async (request: ITemplateGetManyRequest) => {
    return TemplatesCache.execute(request, async () => {
      const { limit, page, name, ...rest } = request as Record<string, unknown>
      const queryParams = this.buildQueryParams({ limit, page, name, ...rest })
      const response = await this.restGet<{ items: IApiTemplate[] }>(
        BASE_PATH,
        queryParams,
      )
      if (!response.items) throw new Error('Invalid response: missing "items"')
      return response.items.map((item) => this.getItem(item))
    })
  }

  getBestTemplate = async (name: string): Promise<IApiTemplate | null> =>
    (await this.getMany({ name, limit: 1000 })).sort(compareByRelevance)[0] ||
    null

  public getItem(template: IApiTemplate) {
    const { uid, name, content, kind } = template
    if (!uid || !name || !kind) {
      throw Error('Invalid response')
    }
    return {
      ...template,
      uid,
      name,
      content: content ?? '',
      kind: UI.CRUD.kindFromJSON(kind),
    }
  }
}
